/**
 * Раздел админки: ИИ-агенты и LLM-провайдеры (схема ai).
 */

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Loader2, Plus, Pencil, Trash2, PlugZap, BarChart2, Settings2 } from "lucide-react";

import { aiAdminApi } from "@/services/ai-admin-api.service";
import type { AgentDefinitionRead, LlmProviderRead, LlmProviderCreatePayload } from "@/types/ai-admin";
import { LlmProviderConfigTabs } from "@/components/admin/LlmProviderConfigTabs";
import {
  compactRuntimeConfig,
  llmProviderRuntimeConfigSchema,
} from "@/lib/llm-provider-runtime-config.schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const QK = ["ai-admin"] as const;

export default function AiAgentsAdminPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [providerDialog, setProviderDialog] = useState<"create" | "edit" | null>(null);
  const [editingProvider, setEditingProvider] = useState<LlmProviderRead | null>(null);
  const [form, setForm] = useState<LlmProviderCreatePayload>({
    name: "",
    provider_kind: "openai_compatible",
    base_url: "",
    default_model: "deepseek-chat",
    is_active: true,
    api_key: "",
    config: {},
  });
  const [stopText, setStopText] = useState("");
  const [extraHeadersText, setExtraHeadersText] = useState("{}");
  const [passthroughText, setPassthroughText] = useState("{}");
  const [providerPendingDelete, setProviderPendingDelete] = useState<LlmProviderRead | null>(null);
  /** Просмотр сводки `GET .../usage-summary` (30 дней). */
  const [usageSummaryProviderId, setUsageSummaryProviderId] = useState<number | null>(null);

  const [agentDialog, setAgentDialog] = useState<AgentDefinitionRead | null>(null);
  const [agentPrompt, setAgentPrompt] = useState("");
  const [agentToolPolicyText, setAgentToolPolicyText] = useState("{}");
  const [agentEnabled, setAgentEnabled] = useState(true);

  const [statsDays, setStatsDays] = useState(7);
  const [runDays, setRunDays] = useState(30);
  const [runAgentFilter, setRunAgentFilter] = useState("");
  const [runStatusFilter, setRunStatusFilter] = useState("");
  const [runOffset, setRunOffset] = useState(0);
  const runLimit = 25;

  const mergeConfigPatch = useCallback((patch: Record<string, unknown>) => {
    setForm((f) => {
      const cur = { ...(f.config || {}) } as Record<string, unknown>;
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined) delete cur[k];
        else cur[k] = v;
      }
      return { ...f, config: cur };
    });
  }, []);

  const { data: providers = [], isLoading: loadingProviders } = useQuery({
    queryKey: [...QK, "llm-providers"],
    queryFn: () => aiAdminApi.listLlmProviders(),
  });

  const { data: globalSettings } = useQuery({
    queryKey: [...QK, "global-settings"],
    queryFn: () => aiAdminApi.getGlobalSettings(),
  });

  const { data: agents = [], isLoading: loadingAgents } = useQuery({
    queryKey: [...QK, "agents"],
    queryFn: () => aiAdminApi.listAgentDefinitions(),
  });

  const { data: providerKinds = [], isLoading: loadingProviderKinds } = useQuery({
    queryKey: [...QK, "llm-provider-kinds"],
    queryFn: () => aiAdminApi.listLlmProviderKinds(),
  });

  const { data: usageSummary, isLoading: usageSummaryLoading } = useQuery({
    queryKey: [...QK, "usage-summary", usageSummaryProviderId],
    queryFn: () => aiAdminApi.getLlmUsageSummary(usageSummaryProviderId!, 30),
    enabled: usageSummaryProviderId != null,
  });

  const { data: agentDashboard, isLoading: loadingAgentDashboard } = useQuery({
    queryKey: [...QK, "agent-dashboard", statsDays],
    queryFn: () => aiAdminApi.getAgentDashboardStats(statsDays),
  });

  const { data: agentRunsPage, isLoading: loadingAgentRuns } = useQuery({
    queryKey: [...QK, "agent-runs", runDays, runAgentFilter, runStatusFilter, runOffset, runLimit],
    queryFn: () =>
      aiAdminApi.listAgentRuns({
        limit: runLimit,
        offset: runOffset,
        days: runDays,
        agent_code: runAgentFilter.trim() || undefined,
        status: runStatusFilter.trim() || undefined,
      }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [...QK] });

  const patchGlobal = useMutation({
    mutationFn: (default_llm_provider_id: number | null) =>
      aiAdminApi.patchGlobalSettings({ default_llm_provider_id }),
    onSuccess: () => {
      toast({ title: "Сохранено", description: "Глобальный провайдер по умолчанию обновлён." });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const createProvider = useMutation({
    mutationFn: (data: LlmProviderCreatePayload) => aiAdminApi.createLlmProvider(data),
    onSuccess: () => {
      toast({ title: "Провайдер создан" });
      setProviderDialog(null);
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const updateProvider = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof aiAdminApi.updateLlmProvider>[1] }) =>
      aiAdminApi.updateLlmProvider(id, data),
    onSuccess: () => {
      toast({ title: "Сохранено" });
      setProviderDialog(null);
      setEditingProvider(null);
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const deleteProvider = useMutation({
    mutationFn: (id: number) => aiAdminApi.deleteLlmProvider(id),
    onSuccess: () => {
      toast({ title: "Удалено" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const testConn = useMutation({
    mutationFn: (id: number) => aiAdminApi.testLlmConnection(id),
    onSuccess: (r) =>
      toast({
        title: r.ok ? "Соединение OK" : "Сбой",
        description: r.message,
        variant: r.ok ? "default" : "destructive",
      }),
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const setAgentDefault = useMutation({
    mutationFn: ({ agentId, pid }: { agentId: number; pid: number }) =>
      aiAdminApi.setAgentDefaultProvider(agentId, pid),
    onSuccess: () => {
      toast({ title: "Привязка обновлена" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const patchAgent = useMutation({
    mutationFn: (payload: {
      id: number;
      enabled?: boolean;
      system_prompt_override?: string | null;
      tool_policy?: Record<string, unknown>;
    }) =>
      aiAdminApi.patchAgentDefinition(payload.id, {
        ...(payload.enabled !== undefined ? { enabled: payload.enabled } : {}),
        ...(payload.system_prompt_override !== undefined ? { system_prompt_override: payload.system_prompt_override } : {}),
        ...(payload.tool_policy !== undefined ? { tool_policy: payload.tool_policy } : {}),
      }),
    onSuccess: () => {
      toast({ title: "Сохранено" });
      invalidate();
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const openAgentDialog = (a: AgentDefinitionRead) => {
    setAgentDialog(a);
    setAgentPrompt(a.system_prompt_override ?? "");
    setAgentToolPolicyText(JSON.stringify(a.tool_policy ?? {}, null, 2));
    setAgentEnabled(a.enabled);
  };

  const submitAgentDialog = () => {
    if (!agentDialog) return;
    let tool_policy: Record<string, unknown>;
    try {
      tool_policy = JSON.parse(agentToolPolicyText || "{}") as Record<string, unknown>;
    } catch {
      toast({
        title: "Некорректный JSON",
        description: "Поле «Политика инструментов (JSON)» должно быть объектом.",
        variant: "destructive",
      });
      return;
    }
    if (typeof tool_policy !== "object" || tool_policy === null || Array.isArray(tool_policy)) {
      toast({ title: "tool_policy", description: "Ожидается JSON-объект.", variant: "destructive" });
      return;
    }
    patchAgent.mutate(
      {
        id: agentDialog.id,
        enabled: agentEnabled,
        system_prompt_override: agentPrompt.trim() ? agentPrompt.trim() : null,
        tool_policy,
      },
      { onSuccess: () => setAgentDialog(null) },
    );
  };

  const openCreate = () => {
    setEditingProvider(null);
    setStopText("");
    setExtraHeadersText("{}");
    setPassthroughText("{}");
    setForm({
      name: "",
      provider_kind: "openai_compatible",
      base_url: "https://api.deepseek.com",
      default_model: "deepseek-chat",
      is_active: true,
      api_key: "",
      config: {},
    });
    setProviderDialog("create");
  };

  const openEdit = (p: LlmProviderRead) => {
    setEditingProvider(p);
    const c = { ...(p.config || {}) } as Record<string, unknown>;
    const st = Array.isArray(c.stop) ? (c.stop as string[]).join("\n") : "";
    delete c.stop;
    const eh = c.extra_headers;
    delete c.extra_headers;
    const pp = c.passthrough_params;
    delete c.passthrough_params;
    setStopText(st);
    setExtraHeadersText(JSON.stringify(eh ?? {}, null, 2));
    setPassthroughText(JSON.stringify(pp ?? {}, null, 2));
    setForm({
      name: p.name,
      provider_kind: p.provider_kind,
      base_url: p.base_url ?? "",
      default_model: p.default_model,
      is_active: p.is_active,
      api_key: "",
      config: c,
    });
    setProviderDialog("edit");
  };

  const confirmDeleteProvider = () => {
    if (!providerPendingDelete || deleteProvider.isPending) return;
    deleteProvider.mutate(providerPendingDelete.id, {
      onSuccess: () => setProviderPendingDelete(null),
    });
  };

  const submitProvider = () => {
    let extra: unknown;
    let passthrough: unknown;
    try {
      extra = JSON.parse(extraHeadersText || "{}");
    } catch {
      toast({
        title: "Некорректный JSON",
        description: "Проверьте поле extra_headers.",
        variant: "destructive",
      });
      return;
    }
    try {
      passthrough = JSON.parse(passthroughText || "{}");
    } catch {
      toast({
        title: "Некорректный JSON",
        description: "Проверьте поле passthrough_params.",
        variant: "destructive",
      });
      return;
    }
    if (typeof extra !== "object" || extra === null || Array.isArray(extra)) {
      toast({
        title: "extra_headers",
        description: "Ожидается JSON-объект с заголовками.",
        variant: "destructive",
      });
      return;
    }
    if (typeof passthrough !== "object" || passthrough === null || Array.isArray(passthrough)) {
      toast({
        title: "passthrough_params",
        description: "Ожидается JSON-объект.",
        variant: "destructive",
      });
      return;
    }

    const stopLines = stopText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const rawCfg: Record<string, unknown> = {
      ...(form.config || {}),
      ...(stopLines.length ? { stop: stopLines } : {}),
      extra_headers: extra as Record<string, string>,
      passthrough_params: passthrough as Record<string, unknown>,
    };

    const zod = llmProviderRuntimeConfigSchema.safeParse(rawCfg);
    if (!zod.success) {
      const msg = zod.error.errors.map((e) => `${e.path.join(".")}: ${e.message}`).join("; ");
      toast({
        title: "Ошибка в параметрах конфигурации",
        description: msg,
        variant: "destructive",
      });
      return;
    }

    const configPayload = compactRuntimeConfig(zod.data as Record<string, unknown>);

    if (providerDialog === "create") {
      createProvider.mutate({
        name: form.name,
        provider_kind: form.provider_kind,
        base_url: form.base_url || null,
        default_model: form.default_model,
        is_active: form.is_active,
        api_key: form.api_key || undefined,
        config: configPayload,
      });
    } else if (providerDialog === "edit" && editingProvider) {
      updateProvider.mutate({
        id: editingProvider.id,
        data: {
          name: form.name,
          provider_kind: form.provider_kind,
          base_url: form.base_url || null,
          default_model: form.default_model,
          is_active: form.is_active,
          api_key: form.api_key || undefined,
          config: configPayload,
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            ИИ-агенты и ассистенты
          </CardTitle>
          <CardDescription>
            Провайдеры LLM (ключи хранятся зашифрованно), глобальный провайдер по умолчанию и привязка агентов.
            Все вызовы <code className="text-xs">/api/v1/ai/*</code> используют только эту конфигурацию. В основном
            приложении в боковой панели «Ассистент ИИ» можно выбрать роль (чат по умолчанию, картограф, отчёты); в
            конструкторе шаблонов — вкладка «Ассистент» для агента «Архитектор шаблонов».
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="providers">
            <TabsList className="mb-4 flex flex-wrap gap-1">
              <TabsTrigger value="providers">Провайдеры</TabsTrigger>
              <TabsTrigger value="defaults">По умолчанию</TabsTrigger>
              <TabsTrigger value="agents">Агенты</TabsTrigger>
              <TabsTrigger value="monitor">Журнал и сводка</TabsTrigger>
            </TabsList>

            <TabsContent value="providers" className="space-y-4">
              <div className="flex justify-end">
                <Button size="sm" onClick={openCreate}>
                  <Plus className="h-4 w-4 mr-1" />
                  Добавить провайдера
                </Button>
              </div>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Имя</TableHead>
                      <TableHead>Тип</TableHead>
                      <TableHead>Модель</TableHead>
                      <TableHead>Ключ</TableHead>
                      <TableHead>Активен</TableHead>
                      <TableHead className="text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingProviders ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                          Загрузка…
                        </TableCell>
                      </TableRow>
                    ) : (
                      providers.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.name}</TableCell>
                          <TableCell className="text-xs max-w-[14rem]">
                            <span className="line-clamp-2" title={p.provider_kind}>
                              {providerKinds.find((k) => k.value === p.provider_kind)?.label ?? p.provider_kind}
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">{p.default_model}</TableCell>
                          <TableCell>{p.has_api_key ? "● задан" : "—"}</TableCell>
                          <TableCell>{p.is_active ? "да" : "нет"}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button
                              variant="outline"
                              size="sm"
                              title="Сводка использования за 30 дней (токены, оценка USD)"
                              onClick={() => setUsageSummaryProviderId(p.id)}
                            >
                              <BarChart2 className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={testConn.isPending || !p.has_api_key}
                              onClick={() => testConn.mutate(p.id)}
                            >
                              <PlugZap className="h-3 w-3 mr-1" />
                              Тест
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => openEdit(p)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => setProviderPendingDelete(p)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="defaults" className="space-y-4 max-w-md">
              <div className="space-y-2">
                <Label>Глобальный LLM по умолчанию</Label>
                <Select
                  value={globalSettings?.default_llm_provider_id != null ? String(globalSettings.default_llm_provider_id) : "none"}
                  onValueChange={(v) =>
                    patchGlobal.mutate(v === "none" ? null : Number(v))
                  }
                  disabled={patchGlobal.isPending}
                >
                  <SelectTrigger className="z-[250]">
                    <SelectValue placeholder="Не задан" />
                  </SelectTrigger>
                  <SelectContent className="z-[250]">
                    <SelectItem value="none">— не задан —</SelectItem>
                    {providers.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name} (id {p.id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Используется маршрутизатором LLM, если у агента нет своей привязки или ключ не задан у привязки.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="agents" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Системный промпт и политика инструментов задаются в карточке агента. Если промпт пуст, используется
                встроенный текст по коду агента (см. документацию). Отключённый агент не обрабатывает запросы в
                чате и анализе геометрии.
              </p>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Код</TableHead>
                      <TableHead>Название</TableHead>
                      <TableHead className="w-[72px]">Вкл</TableHead>
                      <TableHead>Промпт</TableHead>
                      <TableHead>Провайдер</TableHead>
                      <TableHead className="w-[100px] text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingAgents ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          Загрузка…
                        </TableCell>
                      </TableRow>
                    ) : (
                      agents.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-mono text-xs">{a.code}</TableCell>
                          <TableCell>
                            <div className="font-medium">{a.display_name}</div>
                            {a.description ? (
                              <div className="text-xs text-muted-foreground line-clamp-2">{a.description}</div>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Checkbox
                              checked={a.enabled}
                              disabled={patchAgent.isPending}
                              onCheckedChange={(c) =>
                                patchAgent.mutate({ id: a.id, enabled: Boolean(c) })
                              }
                              aria-label={`Агент ${a.code} включён`}
                            />
                          </TableCell>
                          <TableCell>
                            {a.system_prompt_override?.trim() ? (
                              <Badge variant="secondary">свой</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">по умолчанию</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <Select
                              value={
                                a.default_llm_provider_id != null ? String(a.default_llm_provider_id) : "none"
                              }
                              onValueChange={(v) => {
                                if (v === "none") return;
                                setAgentDefault.mutate({ agentId: a.id, pid: Number(v) });
                              }}
                              disabled={setAgentDefault.isPending}
                            >
                              <SelectTrigger className="z-[250]">
                                <SelectValue placeholder="Не задан" />
                              </SelectTrigger>
                              <SelectContent className="z-[250]">
                                <SelectItem value="none">—</SelectItem>
                                {providers.map((p) => (
                                  <SelectItem key={p.id} value={String(p.id)}>
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => openAgentDialog(a)}>
                              <Settings2 className="h-3 w-3 mr-1" />
                              Настройка
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            <TabsContent value="monitor" className="space-y-6">
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-medium">Сводка по запускам</h3>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground whitespace-nowrap">Период</Label>
                    <Select
                      value={String(statsDays)}
                      onValueChange={(v) => setStatsDays(Number(v))}
                    >
                      <SelectTrigger className="z-[250] w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[250]">
                        {[7, 14, 30].map((d) => (
                          <SelectItem key={d} value={String(d)}>
                            {d} дн.
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {loadingAgentDashboard ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Загрузка сводки…
                  </div>
                ) : agentDashboard ? (
                  <>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {(["success", "error", "running"] as const).map((key) => (
                        <Card key={key}>
                          <CardHeader className="py-3">
                            <CardTitle className="text-sm capitalize text-muted-foreground">{key}</CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <p className="text-2xl font-semibold tabular-nums">
                              {agentDashboard.totals_by_status[key] ?? 0}
                            </p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Агент</TableHead>
                            <TableHead>Успех</TableHead>
                            <TableHead>Ошибки</TableHead>
                            <TableHead>В процессе</TableHead>
                            <TableHead>Последнее завершение</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {agentDashboard.per_agent.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                За выбранный период запусков нет.
                              </TableCell>
                            </TableRow>
                          ) : (
                            agentDashboard.per_agent.map((row) => (
                              <TableRow key={row.agent_code}>
                                <TableCell className="font-mono text-xs">{row.agent_code}</TableCell>
                                <TableCell>{row.success_count}</TableCell>
                                <TableCell>
                                  {row.error_count > 0 ? (
                                    <span className="text-destructive font-medium">{row.error_count}</span>
                                  ) : (
                                    row.error_count
                                  )}
                                </TableCell>
                                <TableCell>{row.running_count}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  {row.last_finished_at
                                    ? new Date(row.last_finished_at).toLocaleString("ru-RU")
                                    : "—"}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                ) : null}
              </div>

              <div className="space-y-3 border-t pt-6">
                <h3 className="text-sm font-medium">Журнал запусков</h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div className="space-y-1">
                    <Label className="text-xs">Период (дней)</Label>
                    <Select
                      value={String(runDays)}
                      onValueChange={(v) => {
                        setRunDays(Number(v));
                        setRunOffset(0);
                      }}
                    >
                      <SelectTrigger className="z-[250] w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[250]">
                        <SelectItem value="7">7</SelectItem>
                        <SelectItem value="30">30</SelectItem>
                        <SelectItem value="90">90</SelectItem>
                        <SelectItem value="365">365</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 flex-1 min-w-[8rem]">
                    <Label className="text-xs">Код агента</Label>
                    <Input
                      placeholder="напр. default_chat"
                      value={runAgentFilter}
                      onChange={(e) => {
                        setRunAgentFilter(e.target.value);
                        setRunOffset(0);
                      }}
                    />
                  </div>
                  <div className="space-y-1 w-[10rem]">
                    <Label className="text-xs">Статус</Label>
                    <Select
                      value={runStatusFilter || "all"}
                      onValueChange={(v) => {
                        setRunStatusFilter(v === "all" ? "" : v);
                        setRunOffset(0);
                      }}
                    >
                      <SelectTrigger className="z-[250]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[250]">
                        <SelectItem value="all">Все</SelectItem>
                        <SelectItem value="running">running</SelectItem>
                        <SelectItem value="success">success</SelectItem>
                        <SelectItem value="error">error</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Время</TableHead>
                        <TableHead>Агент</TableHead>
                        <TableHead>Статус</TableHead>
                        <TableHead>Ошибка</TableHead>
                        <TableHead>Пользователь</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loadingAgentRuns ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                            Загрузка…
                          </TableCell>
                        </TableRow>
                      ) : agentRunsPage && agentRunsPage.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                            Записей нет.
                          </TableCell>
                        </TableRow>
                      ) : (
                        agentRunsPage?.items.map((r) => (
                          <TableRow key={r.id}>
                            <TableCell className="font-mono text-xs">{r.id}</TableCell>
                            <TableCell className="text-xs whitespace-nowrap">
                              {new Date(r.started_at).toLocaleString("ru-RU")}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{r.agent_code}</TableCell>
                            <TableCell>
                              {r.status === "success" && (
                                <Badge className="bg-emerald-600 hover:bg-emerald-600">{r.status}</Badge>
                              )}
                              {r.status === "error" && <Badge variant="destructive">{r.status}</Badge>}
                              {r.status === "running" && <Badge variant="secondary">{r.status}</Badge>}
                              {r.status !== "success" && r.status !== "error" && r.status !== "running" && (
                                <Badge variant="outline">{r.status}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="max-w-[14rem] text-xs text-destructive line-clamp-2" title={r.error_message ?? ""}>
                              {r.error_message ?? "—"}
                            </TableCell>
                            <TableCell className="font-mono text-xs max-w-[8rem] truncate" title={r.user_id}>
                              {r.user_id}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                {agentRunsPage && agentRunsPage.total > runLimit ? (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Показано {runOffset + 1}–{Math.min(runOffset + runLimit, agentRunsPage.total)} из{" "}
                      {agentRunsPage.total}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={runOffset === 0}
                        onClick={() => setRunOffset((o) => Math.max(0, o - runLimit))}
                      >
                        Назад
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={runOffset + runLimit >= agentRunsPage.total}
                        onClick={() => setRunOffset((o) => o + runLimit)}
                      >
                        Вперёд
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={providerDialog != null} onOpenChange={() => setProviderDialog(null)}>
        <DialogContent className="z-[220] max-w-2xl max-h-[92vh] flex flex-col gap-0 p-0">
          <div className="p-6 pb-2">
            <DialogHeader>
              <DialogTitle>{providerDialog === "create" ? "Новый провайдер" : "Редактирование провайдера"}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground mt-2">
              Основные поля задают подключение к API. Расширенные параметры (температура, таймауты, инструменты)
              хранятся в JSON-конфигурации провайдера на сервере и применяются ко всем вызовам через этого
              провайдера.
            </p>
          </div>
          <div className="px-6 overflow-y-auto flex-1 min-h-0 space-y-4 pb-2">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Имя</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Например DeepSeek Production"
                />
              </div>
              <div>
                <Label htmlFor="cfg-provider-kind">Тип провайдера</Label>
                {(() => {
                  const currentKind = form.provider_kind ?? "openai_compatible";
                  const catalogValues = new Set(providerKinds.map((k) => k.value));
                  const showLegacyKind = !catalogValues.has(currentKind);
                  const selectedDesc = providerKinds.find((k) => k.value === currentKind)?.description;
                  return (
                    <>
                      <Select
                        disabled={loadingProviderKinds}
                        value={currentKind}
                        onValueChange={(v) => setForm((f) => ({ ...f, provider_kind: v }))}
                      >
                        <SelectTrigger id="cfg-provider-kind" className="z-[250]">
                          <SelectValue placeholder={loadingProviderKinds ? "Загрузка типов…" : "Выберите тип"} />
                        </SelectTrigger>
                        <SelectContent className="z-[250] max-h-[min(60vh,360px)]">
                          {showLegacyKind && (
                            <SelectItem value={currentKind} className="text-amber-700 dark:text-amber-400">
                              Сохранённое (не из каталога): {currentKind}
                            </SelectItem>
                          )}
                          {providerKinds.map((k) => (
                            <SelectItem key={k.value} value={k.value} title={k.description}>
                              {k.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedDesc ? (
                        <p className="text-xs text-muted-foreground mt-1.5">{selectedDesc}</p>
                      ) : showLegacyKind ? (
                        <p className="text-xs text-amber-700 dark:text-amber-400 mt-1.5">
                          Значение из базы не входит в текущий каталог; выберите тип из списка и сохраните провайдера.
                        </p>
                      ) : null}
                    </>
                  );
                })()}
              </div>
              <div>
                <Label>Модель по умолчанию</Label>
                <Input
                  value={form.default_model}
                  onChange={(e) => setForm((f) => ({ ...f, default_model: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Base URL</Label>
                <Input
                  value={form.base_url ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
                  placeholder="https://api.deepseek.com"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>API-ключ {providerDialog === "edit" ? "(оставьте пустым, чтобы не менять)" : ""}</Label>
                <Input
                  type="password"
                  autoComplete="off"
                  value={form.api_key ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <Checkbox
                  checked={form.is_active}
                  onCheckedChange={(c) => setForm((f) => ({ ...f, is_active: Boolean(c) }))}
                />
                <span className="text-sm">Провайдер активен</span>
              </div>
            </div>

            <div className="border rounded-lg p-3 bg-muted/30">
              <p className="text-sm font-medium mb-2">Расширенная конфигурация</p>
              <LlmProviderConfigTabs
                config={(form.config || {}) as Record<string, unknown>}
                onConfigChange={mergeConfigPatch}
                stopText={stopText}
                onStopTextChange={setStopText}
                extraHeadersText={extraHeadersText}
                onExtraHeadersTextChange={setExtraHeadersText}
                passthroughText={passthroughText}
                onPassthroughTextChange={setPassthroughText}
              />
            </div>
          </div>
          <DialogFooter className="p-6 pt-4 border-t bg-background shrink-0">
            <Button variant="outline" onClick={() => setProviderDialog(null)}>
              Отмена
            </Button>
            <Button
              onClick={submitProvider}
              disabled={createProvider.isPending || updateProvider.isPending || !form.name.trim()}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={usageSummaryProviderId != null}
        onOpenChange={(open) => {
          if (!open) setUsageSummaryProviderId(null);
        }}
      >
        <DialogContent className="z-[220] max-w-md">
          <DialogHeader>
            <DialogTitle>
              Использование LLM
              {usageSummaryProviderId != null
                ? ` — ${providers.find((x) => x.id === usageSummaryProviderId)?.name ?? ""}`
                : ""}
            </DialogTitle>
            <DialogDescription>
              Скользящие 30 дней по журналу вызовов. Оценка USD — из полей{" "}
              <code className="text-xs">cost_per_1k_input_tokens</code> /{" "}
              <code className="text-xs">cost_per_1k_output_tokens</code> в конфигурации провайдера, если заданы.
            </DialogDescription>
          </DialogHeader>
          {usageSummaryLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Загрузка…
            </div>
          ) : usageSummary ? (
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Период с</dt>
                <dd className="font-mono text-xs">{usageSummary.since}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Запросов</dt>
                <dd>{usageSummary.total_requests}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Prompt tokens</dt>
                <dd>{usageSummary.sum_prompt_tokens.toLocaleString("ru-RU")}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Completion tokens</dt>
                <dd>{usageSummary.sum_completion_tokens.toLocaleString("ru-RU")}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Всего токенов</dt>
                <dd>{usageSummary.sum_total_tokens.toLocaleString("ru-RU")}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Оценка стоимости (USD)</dt>
                <dd>
                  {usageSummary.sum_estimated_cost_usd != null
                    ? Number(usageSummary.sum_estimated_cost_usd).toLocaleString("ru-RU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })
                    : "—"}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">Нет данных.</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUsageSummaryProviderId(null)}>
              Закрыть
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={agentDialog != null}
        onOpenChange={(open) => {
          if (!open) setAgentDialog(null);
        }}
      >
        <DialogContent className="z-[220] max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Агент: {agentDialog?.display_name ?? ""}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{agentDialog?.code}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-1">
            <div className="flex items-center gap-2">
              <Checkbox
                id="agent-enabled"
                checked={agentEnabled}
                onCheckedChange={(c) => setAgentEnabled(Boolean(c))}
              />
              <Label htmlFor="agent-enabled">Агент включён</Label>
            </div>
            <div className="space-y-1">
              <Label>Системный промпт (переопределение)</Label>
              <Textarea
                value={agentPrompt}
                onChange={(e) => setAgentPrompt(e.target.value)}
                placeholder="Оставьте пустым, чтобы использовать встроенный промпт по коду агента"
                rows={6}
                className="font-mono text-xs"
              />
              <p className="text-xs text-muted-foreground">
                Пустое поле — используется текст по умолчанию из сервера для данного кода.
              </p>
            </div>
            <div className="space-y-1">
              <Label>Политика инструментов (JSON)</Label>
              <Textarea
                value={agentToolPolicyText}
                onChange={(e) => setAgentToolPolicyText(e.target.value)}
                rows={4}
                className="font-mono text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAgentDialog(null)}>
              Отмена
            </Button>
            <Button
              onClick={submitAgentDialog}
              disabled={patchAgent.isPending || !agentDialog}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        isOpen={providerPendingDelete != null}
        title="Удалить провайдера?"
        description={
          providerPendingDelete
            ? `Будет удалён провайдер «${providerPendingDelete.name}». Это действие нельзя отменить.`
            : ""
        }
        onConfirm={confirmDeleteProvider}
        onCancel={() => setProviderPendingDelete(null)}
        confirmText="Удалить"
        variant="destructive"
      />
    </div>
  );
}
